'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarRange, KanbanSquare, Activity, RefreshCw, AlertTriangle, Eye, Rocket, GanttChartSquare, CalendarDays, FolderKanban, Map as MapIcon } from 'lucide-react'
import { effectiveTaskStatus } from '@/lib/utils'
import {
  activeSprint, taskInSprint, weekOfSprint, workload, velocity, burndown,
  sprintHealth, pointsOf, isoDay, diffDays, type RiskCtx, type Health,
} from '@/lib/dev-sprint'
import { DEV_CAPACITY_POINTS } from '@/lib/dev-team'
import { useToast } from '@/context/ToastContext'
import type { Task, Subtask } from '@/lib/types'
import type { BoardData, Filters, DevDerived } from './types'
import { EMPTY_FILTERS } from './types'
import { SprintTopBar } from './SprintTopBar'
import { FilterBar } from './FilterBar'
import { KpiRow } from './KpiRow'
import { WeekFocusStrip } from './WeekFocusStrip'
import { ProjectOverview } from './ProjectOverview'
import { TimelineView } from './TimelineView'
import { KanbanView } from './KanbanView'
import { MonitoringView } from './MonitoringView'
import { GanttTimeline } from './GanttTimeline'
import { CalendarView } from './CalendarView'
import { ProjectsView } from './ProjectsView'
import { RoadmapView } from './RoadmapView'
import { TaskDrawer } from './TaskDrawer'
import { NewSprintModal, NewTaskModal } from './Modals'

type ModalState = { kind: 'sprint' } | { kind: 'task'; devUsername?: string | null; week?: number; projectId?: string; dueDate?: string } | null
type Section = 'sprint' | 'projects' | 'kanban' | 'timeline' | 'roadmap' | 'calendar'
type SubTab = 'week' | 'monitoring'
const norm = (x?: string | null) => (x ?? '').trim().toLowerCase()
const nowISO = () => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10)

interface Props {
  meEmail: string
  meUsername: string | null
  isLead: boolean
  isSupervisor: boolean
}

export function DevSprintApp({ meUsername, isLead, isSupervisor }: Props) {
  const [board, setBoard] = useState<BoardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sprintId, setSprintId] = useState<string | null>(null)
  const [section, setSection] = useState<Section>(isSupervisor ? 'timeline' : 'sprint')
  const [subTab, setSubTab] = useState<SubTab>(isSupervisor ? 'monitoring' : 'week')
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [drawerId, setDrawerId] = useState<string | null>(null)
  const [modal, setModal] = useState<ModalState>(null)
  const readOnly = isSupervisor
  const today = nowISO()
  const { toast } = useToast()

  const canEditTaskFn = (assignee: string | null) => {
    if (isSupervisor) return false
    if (isLead) return true
    return !!meUsername && norm(assignee) === norm(meUsername)
  }

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await fetch('/api/dev/board', { cache: 'no-store' })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `Gagal memuat (${res.status})`)
      }
      setBoard((await res.json()) as BoardData)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => { load() }, [load])

  const afterMutation = useCallback(() => {
    setDrawerId(null)
    setModal(null)
    load(true)
  }, [load])

  const openProject = useCallback((id: string) => {
    setFilters((f) => ({ ...f, project: id }))
    setSection('projects')
  }, [])

  const deleteSprint = useCallback(async (id: string) => {
    try {
      const res = await fetch(`/api/sprints/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Gagal menghapus sprint')
      toast('Sprint dihapus', 'success')
      setSprintId(null)
      load(true)
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Gagal', 'error')
    }
  }, [load, toast])

  // Default sprint = aktif (memuat hari ini), fallback terbaru.
  useEffect(() => {
    if (board && sprintId === null && board.sprints.length) {
      const a = activeSprint(board.sprints, today)
      setSprintId(a?.id ?? board.sprints[board.sprints.length - 1].id)
    }
  }, [board, sprintId, today])

  const sprint = useMemo(() => board?.sprints.find((s) => s.id === sprintId) ?? null, [board, sprintId])

  const derived: DevDerived | null = useMemo(() => {
    if (!board) return null
    const subsByTask = new Map<string, Subtask[]>()
    for (const s of board.subtasks) {
      const arr = subsByTask.get(s.taskId) ?? []
      arr.push(s)
      subsByTask.set(s.taskId, arr)
    }
    const statusOf = (t: Task) => effectiveTaskStatus(t.status, subsByTask.get(t.id) ?? [])
    const projById = new Map(board.projects.map((p) => [p.id, p]))
    const doneProject = new Set<string>()
    for (const p of board.projects) {
      const pts = board.tasks.filter((t) => t.projectId === p.id)
      if (pts.length && pts.every((t) => statusOf(t) === 'done')) doneProject.add(p.id)
    }
    const isProjectBlocked = (pid: string) => {
      const dep = projById.get(pid)?.dependsOn
      return !!dep && projById.has(dep) && !doneProject.has(dep)
    }
    const overloadedUsers = new Set<string>()
    // Overload cuma dihitung kalau HARI INI ada di dalam sprint terpilih (bukan sprint lampau/mendatang).
    const cw = sprint ? weekOfSprint(today, sprint) : null
    if (sprint && cw) {
      const usernames = board.team.map((m) => m.username ?? '').filter(Boolean)
      const inSprint = board.tasks.filter((t) => taskInSprint(t, sprint))
      const wl = workload(inSprint, sprint, usernames, statusOf, cw)
      for (const [u, arr] of Object.entries(wl)) {
        if ((arr[cw - 1] ?? 0) > DEV_CAPACITY_POINTS) overloadedUsers.add(u)
      }
    }
    return { statusOf, subsByTask, projById, todayISO: today, isProjectBlocked, overloadedUsers }
  }, [board, sprint, today])

  const riskCtx: RiskCtx | null = useMemo(
    () => (derived ? {
      todayISO: today,
      statusOf: derived.statusOf,
      isProjectBlocked: derived.isProjectBlocked,
      overloadedUsers: derived.overloadedUsers,
    } : null),
    [derived, today],
  )

  const metrics = useMemo(() => {
    if (!board || !sprint || !derived) return null
    const inSprint = board.tasks.filter((t) => taskInSprint(t, sprint))
    const totalPts = inSprint.reduce((a, t) => a + pointsOf(t), 0)
    const donePts = inSprint.reduce((a, t) => a + (derived.statusOf(t) === 'done' ? pointsOf(t) : 0), 0)
    let doneCount = 0, onTime = 0
    for (const t of inSprint) {
      if (derived.statusOf(t) !== 'done') continue
      doneCount++
      const due = isoDay(t.dueDate)
      const fin = isoDay(t.tanggalSelesai) || due
      if (!due || !fin || fin <= due) onTime++
    }
    return {
      totalPts,
      donePts,
      progressPct: totalPts ? Math.round((donePts / totalPts) * 100) : 0,
      teamOnTimePct: doneCount ? Math.round((onTime / doneCount) * 100) : null,
      burndownSeries: burndown(sprint, board.tasks, derived.statusOf, today),
      velocityData: velocity(board.sprints, board.tasks, derived.statusOf, today),
    }
  }, [board, sprint, derived, today])

  const health: Health = useMemo(
    () => (sprint && board && riskCtx ? sprintHealth(sprint, board.tasks, riskCtx) : 'on-track'),
    [sprint, board, riskCtx],
  )
  const daysLeft = sprint ? diffDays(today, isoDay(sprint.endDate)) : null

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return <Centered><RefreshCw className="w-5 h-5 animate-spin text-[#AEADA8]" /> <span className="text-sm text-[#8B8A85]">Memuat…</span></Centered>
  }
  if (error) {
    return (
      <Centered>
        <AlertTriangle className="w-5 h-5 text-[#C0362C]" />
        <span className="text-sm text-[#C0362C]">{error}</span>
        <button onClick={() => load()} className="ml-2 text-sm underline text-[#1A1916]">Coba lagi</button>
      </Centered>
    )
  }
  if (!board || !derived || !riskCtx) return null

  const sections: { key: Section; label: string; Icon: typeof Rocket }[] = [
    { key: 'sprint', label: 'Dev Sprint', Icon: Rocket },
    { key: 'projects', label: 'Projects', Icon: FolderKanban },
    { key: 'kanban', label: 'Kanban', Icon: KanbanSquare },
    { key: 'timeline', label: 'Timeline', Icon: GanttChartSquare },
    { key: 'roadmap', label: 'Roadmap', Icon: MapIcon },
    { key: 'calendar', label: 'Calendar', Icon: CalendarDays },
  ]
  const subTabs: { key: SubTab; label: string; Icon: typeof CalendarRange }[] = [
    { key: 'week', label: 'Papan Minggu', Icon: CalendarRange },
    { key: 'monitoring', label: 'Monitoring', Icon: Activity },
  ]
  const filterBar = <FilterBar filters={filters} setFilters={setFilters} team={board.team} projects={board.projects} />

  return (
    <div className="min-h-screen px-5 md:px-8 py-6 max-w-[1400px] mx-auto">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-extrabold text-[#1A1916] tracking-tight">Dev Sprint</h1>
          <p className="text-[13px] text-[#8B8A85] mt-0.5">Perencanaan sprint, timeline & monitoring on-time tim development.</p>
        </div>
        {readOnly && (
          <span className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#F1F1EF] text-[#6B6A65] border border-[#E1DFD8]">
            <Eye className="w-3.5 h-3.5" /> Read-only
          </span>
        )}
      </header>

      {/* Head tabs (section) */}
      <div className="inline-flex rounded-xl border border-[#E7E5DF] bg-[#F7F6F2] p-1 mb-4">
        {sections.map(({ key, label, Icon }) => {
          const active = section === key
          return (
            <button
              key={key}
              onClick={() => setSection(key)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-semibold transition-colors ${active ? 'bg-white text-[#1A1916] shadow-sm' : 'text-[#8B8A85] hover:text-[#1A1916]'}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          )
        })}
      </div>

      {/* ── SECTION: Dev Sprint ── */}
      {section === 'sprint' && (
        <>
          <SprintTopBar
            sprints={board.sprints}
            sprint={sprint}
            health={health}
            daysLeft={daysLeft}
            isLead={isLead}
            readOnly={readOnly}
            onSelect={setSprintId}
            onRefresh={() => load(true)}
            onNewSprint={() => setModal({ kind: 'sprint' })}
            onNewTask={() => setModal({ kind: 'task' })}
            onDeleteSprint={deleteSprint}
          />

          {!sprint ? (
            <div className="mt-4"><EmptyState isLead={isLead} /></div>
          ) : (
            <>
              {metrics && <KpiRow metrics={metrics} capacity={DEV_CAPACITY_POINTS} selectedSprintId={sprintId} />}
              <WeekFocusStrip board={board} sprint={sprint} derived={derived} today={today} onOpenTask={setDrawerId} />
              <ProjectOverview board={board} derived={derived} activeProject={filters.project} onFilterProject={(id) => setFilters({ ...filters, project: id })} />

              {/* Sub-tabs */}
              <div className="mt-6 flex items-center gap-1 border-b border-[#E7E5DF]">
                {subTabs.map(({ key, label, Icon }) => {
                  const active = subTab === key
                  return (
                    <button
                      key={key}
                      onClick={() => setSubTab(key)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors ${active ? 'border-[#1A1916] text-[#1A1916]' : 'border-transparent text-[#8B8A85] hover:text-[#1A1916]'}`}
                    >
                      <Icon className="w-4 h-4" /> {label}
                    </button>
                  )
                })}
              </div>
              {filterBar}
              <div className="mt-4">
                {subTab === 'week' ? (
                  <TimelineView
                    board={board} sprint={sprint} derived={derived} riskCtx={riskCtx} filters={filters}
                    isLead={isLead && !readOnly} readOnly={readOnly} meUsername={meUsername} canEditTaskFn={canEditTaskFn}
                    onOpenTask={setDrawerId}
                    onAddTask={(devUsername, week) => setModal({ kind: 'task', devUsername, week })}
                    onChanged={afterMutation}
                  />
                ) : (
                  <MonitoringView
                    board={board} sprint={sprint} derived={derived} riskCtx={riskCtx}
                    burndownSeries={metrics?.burndownSeries ?? []} velocityData={metrics?.velocityData ?? []}
                    capacity={DEV_CAPACITY_POINTS} onOpenTask={setDrawerId}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ── SECTION: Projects (mulai → target → progress mingguan) ── */}
      {section === 'projects' && (
        <>
          {filterBar}
          <div className="mt-4">
            <ProjectsView board={board} derived={derived} filters={filters} today={today} isLead={isLead && !readOnly} onOpenTask={setDrawerId} onAddTask={(projectId) => setModal({ kind: 'task', projectId })} onChanged={afterMutation} />
          </div>
        </>
      )}

      {/* ── SECTION: Kanban (To Do / In Progress / Review / Done) ── */}
      {section === 'kanban' && (
        <>
          {filterBar}
          <div className="mt-4">
            <KanbanView
              board={board} sprint={sprint} derived={derived} riskCtx={riskCtx} filters={filters}
              canEditTaskFn={canEditTaskFn} isLead={isLead && !readOnly} readOnly={readOnly}
              onOpenTask={setDrawerId} onChanged={afterMutation}
            />
          </div>
        </>
      )}

      {/* ── SECTION: Timeline (Gantt) ── */}
      {section === 'timeline' && (
        <>
          {filterBar}
          <div className="mt-4">
            <GanttTimeline board={board} derived={derived} riskCtx={riskCtx} filters={filters} today={today} isLead={isLead && !readOnly} onOpenTask={setDrawerId} onChanged={afterMutation} />
          </div>
        </>
      )}

      {/* ── SECTION: Roadmap (level project) ── */}
      {section === 'roadmap' && (
        <>
          {filterBar}
          <div className="mt-4">
            <RoadmapView
              board={board} derived={derived} filters={filters} today={today}
              isLead={isLead && !readOnly} onOpenProject={openProject} onChanged={afterMutation}
            />
          </div>
        </>
      )}

      {/* ── SECTION: Calendar ── */}
      {section === 'calendar' && (
        <>
          {filterBar}
          <div className="mt-4">
            <CalendarView board={board} derived={derived} riskCtx={riskCtx} filters={filters} today={today} isLead={isLead && !readOnly} onOpenTask={setDrawerId} onAddTask={(dueDate) => setModal({ kind: 'task', dueDate })} />
          </div>
        </>
      )}

      {/* Drawer detail task (global) */}
      {drawerId && (() => {
        const t = board.tasks.find((x) => x.id === drawerId)
        if (!t) return null
        return (
          <TaskDrawer
            task={t}
            project={derived.projById.get(t.projectId)}
            team={board.team}
            subtasks={derived.subsByTask.get(t.id) ?? []}
            sprints={board.sprints}
            canEdit={canEditTaskFn(t.assignee)}
            isLead={isLead && !readOnly}
            onClose={() => setDrawerId(null)}
            onSaved={afterMutation}
          />
        )
      })()}

      {/* Modals (global) */}
      {modal?.kind === 'sprint' && <NewSprintModal onClose={() => setModal(null)} onDone={afterMutation} />}
      {modal?.kind === 'task' && (
        <NewTaskModal
          projects={board.projects}
          team={board.team}
          sprint={sprint}
          prefill={{ devUsername: modal.devUsername, week: modal.week, projectId: modal.projectId, dueDate: modal.dueDate }}
          onClose={() => setModal(null)}
          onDone={afterMutation}
        />
      )}
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="min-h-[60vh] flex items-center justify-center gap-2">{children}</div>
}

function EmptyState({ isLead }: { isLead: boolean }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#D8D6CF] bg-white/60 py-16 text-center">
      <p className="text-[15px] font-semibold text-[#1A1916]">Belum ada sprint</p>
      <p className="text-[13px] text-[#8B8A85] mt-1">
        {isLead ? 'Buat sprint pertama lewat tombol “+ Sprint” di atas.' : 'Menunggu lead membuat sprint.'}
      </p>
    </div>
  )
}
